/**
 * W3C Trace Context header helpers.
 *
 * F4 of epic 2026-05-26_export-flow-and-observability — both the
 * Connect-RPC transport interceptor (gRPC-Web → plugin) and the axios
 * apiClient request interceptor (REST → API) call into here to stamp
 * every outbound request with `traceparent: 00-<32hex>-<16hex>-01`.
 *
 * Decision: we deliberately do NOT pull in `@opentelemetry/api` or the
 * browser SDK — the bundle cost outweighs the benefit and a "virtual"
 * React span can be reconstructed from Loki logs alone (per epic brief
 * §3.3). All we need on the wire is the header.
 *
 * The helpers are intentionally tree-shake-friendly (no top-level
 * side effects) so apiClient.ts can import them without dragging the
 * Connect-RPC types into the apiClient module graph.
 */

const HEX = '0123456789abcdef';

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i];
    out += HEX[(b >> 4) & 0xf];
    out += HEX[b & 0xf];
  }
  return out;
}

function randomBytes(length: number): Uint8Array {
  const out = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(out);
    return out;
  }
  for (let i = 0; i < length; i += 1) {
    out[i] = Math.floor(Math.random() * 256);
  }
  return out;
}

/**
 * Generate a fresh W3C trace-id: 16 random bytes → 32 lowercase hex
 * characters. Never returns an all-zero id (spec-forbidden) — on the
 * astronomically unlikely RNG output of 16 zero bytes, retries once.
 */
export function generateTraceId(): string {
  let bytes = randomBytes(16);
  let hex = bytesToHex(bytes);
  if (hex === '0'.repeat(32)) {
    bytes = randomBytes(16);
    bytes[0] = bytes[0] | 0x01;
    hex = bytesToHex(bytes);
  }
  return hex;
}

/**
 * Generate a fresh W3C span-id: 8 random bytes → 16 lowercase hex
 * characters. Spec also forbids all-zero — same retry tactic.
 */
export function generateSpanId(): string {
  let bytes = randomBytes(8);
  let hex = bytesToHex(bytes);
  if (hex === '0'.repeat(16)) {
    bytes = randomBytes(8);
    bytes[0] = bytes[0] | 0x01;
    hex = bytesToHex(bytes);
  }
  return hex;
}

/**
 * Assemble a W3C traceparent header value
 * (`version-traceId-spanId-traceFlags`). Trace-flags is `01` (sampled)
 * — we keep 100 % sampling on this fleet (per epic NFR §5).
 */
export function makeTraceparent(traceId?: string, spanId?: string): string {
  const tId = traceId && /^[0-9a-f]{32}$/i.test(traceId) ? traceId.toLowerCase() : generateTraceId();
  const sId = spanId && /^[0-9a-f]{16}$/i.test(spanId) ? spanId.toLowerCase() : generateSpanId();
  return `00-${tId}-${sId}-01`;
}

/**
 * RegExp matching a syntactically-valid W3C traceparent string. Used
 * by tests and defensive callers. Matches version 00 only — that's all
 * the spec currently defines.
 */
export const TRACEPARENT_REGEX = /^00-[0-9a-f]{32}-[0-9a-f]{16}-[0-9a-f]{2}$/;
